import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Search, MapPin, CalendarDays, Plus, Edit2, Trash2, Loader2, MessageCircle, Globe, DollarSign, ExternalLink, Sparkles, Users } from 'lucide-react';

const EVENT_TYPES = ['', 'Horse Show', 'Poker Ride', 'Trail Ride', 'Clinic', 'Auction', 'Rodeo', 'Expo', 'Camp', 'Fundraiser', 'Other'];
const US_STATES = ['','AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const TYPE_COLORS = {
  'Horse Show': { bg: '#E9EFE8', text: '#2D6A4F' },
  'Poker Ride': { bg: '#FEF3C7', text: '#92400E' },
  'Trail Ride': { bg: '#D1FAE5', text: '#065F46' },
  'Clinic': { bg: '#DBEAFE', text: '#1E3A5F' },
  'Auction': { bg: '#FEE2E2', text: '#991B1B' },
  'Rodeo': { bg: '#FCE7F3', text: '#831843' },
  'Expo': { bg: '#EDE9FE', text: '#4C1D95' },
  'Camp': { bg: '#F9F7F3', text: '#5C4A2F' },
  'Fundraiser': { bg: '#FEF3C7', text: '#78350F' },
  'Other': { bg: '#F3F4F6', text: '#374151' },
};

function EventCard({ event, onChat, onEdit, onDelete, isOwner, isCommunity }) {
  const tc = TYPE_COLORS[event.event_type] || TYPE_COLORS['Other'];
  const eventDate = event.date ? new Date(event.date + 'T12:00:00') : null;
  const dateStr = eventDate ? eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="bg-white rounded-[1.5rem] p-5 shadow-[0_4px_12px_rgb(27,67,50,0.04)] hover:shadow-[0_8px_20px_rgb(27,67,50,0.08)] transition-shadow" data-testid={`event-card-${event.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: tc.bg, color: tc.text }}>
              {event.event_type}
            </span>
            {event.source_type === 'ai_discovered' && (
              <span className="px-2 py-0.5 rounded-full bg-[#EDE9FE] text-[#4C1D95] text-[10px] font-medium flex items-center gap-1">
                <Sparkles size={8} /> AI Found
              </span>
            )}
          </div>
          <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] leading-tight">{event.title}</h3>
        </div>
        {isOwner && (
          <div className="flex gap-1 ml-2 flex-shrink-0">
            <button onClick={() => onEdit(event)} className="w-7 h-7 rounded-full bg-[#E9EFE8] flex items-center justify-center text-[#4B6B58] hover:text-[#1B4332]" data-testid={`edit-event-${event.id}`}>
              <Edit2 size={12} />
            </button>
            <button onClick={() => onDelete(event.id)} className="w-7 h-7 rounded-full bg-[#FEE2E2] flex items-center justify-center text-red-400 hover:text-red-600" data-testid={`delete-event-${event.id}`}>
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>

      {event.description && <p className="text-sm text-[#4B6B58] mb-3 line-clamp-2">{event.description}</p>}

      <div className="space-y-1.5 mb-3">
        {dateStr && (
          <div className="flex items-center gap-2 text-sm text-[#122A1F]">
            <CalendarDays size={13} className="text-[#4B6B58] flex-shrink-0" />
            <span>{dateStr}{event.end_date ? ` – ${new Date(event.end_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</span>
          </div>
        )}
        {(event.location || event.address) && (
          <div className="flex items-center gap-2 text-sm text-[#122A1F]">
            <MapPin size={13} className="text-[#4B6B58] flex-shrink-0" />
            <span className="truncate">{event.location}{event.address ? ` — ${event.address}` : ''}</span>
          </div>
        )}
        {event.cost && (
          <div className="flex items-center gap-2 text-sm text-[#122A1F]">
            <DollarSign size={13} className="text-[#4B6B58] flex-shrink-0" />
            <span>{event.cost}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-[#E9EFE8]">
        {event.website && (
          <a href={event.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-[#2D6A4F] bg-[#E9EFE8] hover:bg-[#D1E0D5] transition-colors" data-testid={`event-website-${event.id}`}>
            <ExternalLink size={10} /> Website
          </a>
        )}
        {isCommunity && onChat && (
          <button onClick={() => onChat(event)} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-white bg-[#1B4332] hover:bg-[#2D6A4F] transition-colors ml-auto" data-testid={`event-chat-${event.id}`}>
            <MessageCircle size={10} /> Group Chat
          </button>
        )}
        {isCommunity && event.user_name && (
          <span className="text-[10px] text-[#4B6B58] ml-auto">Posted by {event.user_name}</span>
        )}
      </div>
    </div>
  );
}

export default function EventsPage() {
  const { authHeaders, API, user, hasFullAccess } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('discover');

  // Discover state
  const [zipCode, setZipCode] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchRadius, setSearchRadius] = useState(50);
  const [discoveredEvents, setDiscoveredEvents] = useState([]);
  const [discovering, setDiscovering] = useState(false);

  // Community state
  const [communityEvents, setCommunityEvents] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(true);
  const [filterState, setFilterState] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterZip, setFilterZip] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({
    title: '', event_type: 'Horse Show', description: '', date: '', end_date: '',
    location: '', address: '', zip_code: '', state: '', contact_info: '',
    website: '', cost: '', notes: ''
  });

  const fetchCommunityEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterZip) params.append('zip_code', filterZip);
      if (filterState) params.append('state', filterState);
      if (filterType) params.append('event_type', filterType);
      const res = await axios.get(`${API}/community-events?${params}`, authHeaders());
      setCommunityEvents(res.data);
    } catch { /* silent */ }
    finally { setCommunityLoading(false); }
  }, [API, authHeaders, filterZip, filterState, filterType]);

  useEffect(() => { fetchCommunityEvents(); }, [fetchCommunityEvents]);

  const handleDiscover = async () => {
    if (!zipCode || zipCode.length < 5) {
      toast.error('Please enter a valid zip code');
      return;
    }
    setDiscovering(true);
    try {
      const res = await axios.post(`${API}/events/discover`, {
        zip_code: zipCode,
        event_type: searchType,
        radius_miles: searchRadius
      }, authHeaders());
      setDiscoveredEvents(res.data.events || []);
      if (res.data.error) toast.error(res.data.error);
      else if ((res.data.events || []).length === 0) toast.info('No events found. Try a larger radius or different event type.');
      else toast.success(`Found ${res.data.events.length} events near ${zipCode}!`);
    } catch (err) {
      toast.error('Failed to search for events');
    } finally {
      setDiscovering(false);
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      if (editEvent) {
        await axios.put(`${API}/community-events/${editEvent.id}`, form, authHeaders());
        toast.success('Event updated!');
      } else {
        await axios.post(`${API}/community-events`, form, authHeaders());
        toast.success('Event posted!');
      }
      setShowCreateDialog(false);
      setEditEvent(null);
      resetForm();
      fetchCommunityEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/community-events/${eventId}`, authHeaders());
      toast.success('Event deleted');
      fetchCommunityEvents();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEventChat = async (event) => {
    try {
      const res = await axios.post(`${API}/events/${event.id}/chat`, {}, authHeaders());
      toast.success(`Joined "${res.data.name}" chat!`);
      navigate(`/chat/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to create/join event chat');
    }
  };

  const openEdit = (event) => {
    setEditEvent(event);
    setForm({
      title: event.title || '', event_type: event.event_type || 'Other',
      description: event.description || '', date: event.date || '',
      end_date: event.end_date || '', location: event.location || '',
      address: event.address || '', zip_code: event.zip_code || '',
      state: event.state || '', contact_info: event.contact_info || '',
      website: event.website || '', cost: event.cost || '', notes: event.notes || ''
    });
    setShowCreateDialog(true);
  };

  const resetForm = () => setForm({
    title: '', event_type: 'Horse Show', description: '', date: '', end_date: '',
    location: '', address: '', zip_code: '', state: '', contact_info: '',
    website: '', cost: '', notes: ''
  });

  // Save a discovered event to community board
  const saveDiscoveredEvent = async (event) => {
    try {
      await axios.post(`${API}/community-events`, {
        title: event.title, event_type: event.event_type || 'Other',
        description: event.description || '', date: event.date || '',
        end_date: event.end_date || '', location: event.location || '',
        address: event.address || '', zip_code: zipCode, state: '',
        contact_info: '', website: event.website || '',
        cost: event.cost || '', notes: `Source: ${event.source || 'AI Discovery'}`
      }, authHeaders());
      toast.success(`"${event.title}" saved to Community Board!`);
      fetchCommunityEvents();
    } catch (err) {
      toast.error('Failed to save event');
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 animate-fade-in">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="events-title">
            Local Equine Events
          </h1>
          <p className="text-[#4B6B58] mt-1">Discover events near you or share with the community.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="inline-flex h-auto p-1.5 bg-white rounded-[1.5rem] shadow-[0_4px_12px_rgb(27,67,50,0.04)] gap-1 mb-6" data-testid="events-tabs">
            <TabsTrigger
              value="discover"
              data-testid="tab-discover"
              className="flex items-center gap-2 px-5 py-2.5 rounded-[1rem] text-sm font-medium data-[state=active]:bg-[#1B4332] data-[state=active]:text-white text-[#4B6B58]"
            >
              <Sparkles size={15} /> Discover Events
            </TabsTrigger>
            <TabsTrigger
              value="community"
              data-testid="tab-community"
              className="flex items-center gap-2 px-5 py-2.5 rounded-[1rem] text-sm font-medium data-[state=active]:bg-[#1B4332] data-[state=active]:text-white text-[#4B6B58]"
            >
              <Users size={15} /> Community Board
            </TabsTrigger>
          </TabsList>

          {/* DISCOVER TAB */}
          <TabsContent value="discover" className="animate-fade-in">
            <div className="bg-white rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)] mb-6" data-testid="discover-search">
              <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-4">Find Events Near You</h2>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-[#4B6B58] mb-1">Zip Code *</label>
                  <input
                    value={zipCode}
                    onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="e.g. 73301"
                    data-testid="discover-zip-input"
                    className="stable-input w-32"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4B6B58] mb-1">Event Type</label>
                  <select value={searchType} onChange={e => setSearchType(e.target.value)} data-testid="discover-type-select" className="stable-input">
                    <option value="">All Types</option>
                    {EVENT_TYPES.filter(t => t).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4B6B58] mb-1">Radius</label>
                  <select value={searchRadius} onChange={e => setSearchRadius(parseInt(e.target.value))} data-testid="discover-radius-select" className="stable-input">
                    <option value={25}>25 miles</option>
                    <option value={50}>50 miles</option>
                    <option value={100}>100 miles</option>
                    <option value={200}>200 miles</option>
                  </select>
                </div>
                <button
                  onClick={handleDiscover}
                  disabled={discovering}
                  data-testid="discover-search-button"
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
                >
                  {discovering ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  {discovering ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            {discoveredEvents.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[#4B6B58] font-medium">{discoveredEvents.length} events found near {zipCode}</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discovered-events-grid">
                  {discoveredEvents.map(event => (
                    <div key={event.id} className="relative">
                      <EventCard event={event} isCommunity={false} />
                      {hasFullAccess && (
                        <button
                          onClick={() => saveDiscoveredEvent(event)}
                          data-testid={`save-event-${event.id}`}
                          className="absolute top-3 right-3 flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1B4332] text-white text-[10px] font-semibold hover:bg-[#2D6A4F] shadow-md"
                        >
                          <Plus size={10} /> Save to Board
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : !discovering ? (
              <div className="bg-white rounded-[2rem] p-10 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]">
                <Globe size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
                <p className="text-sm text-[#4B6B58]">Enter your zip code and hit Search to discover equine events nearby.</p>
                <p className="text-xs text-[#4B6B58] mt-1">AI scans for horse shows, poker rides, trail rides, clinics, and more.</p>
              </div>
            ) : null}
          </TabsContent>

          {/* COMMUNITY BOARD TAB */}
          <TabsContent value="community" className="animate-fade-in">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <input
                value={filterZip}
                onChange={e => setFilterZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Filter by zip"
                data-testid="community-zip-filter"
                className="stable-input w-28 text-sm"
              />
              <select value={filterState} onChange={e => setFilterState(e.target.value)} data-testid="community-state-filter" className="stable-input text-sm">
                <option value="">All States</option>
                {US_STATES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="community-type-filter" className="stable-input text-sm">
                <option value="">All Types</option>
                {EVENT_TYPES.filter(t => t).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <div className="ml-auto">
                {hasFullAccess && (
                  <button
                    onClick={() => { setEditEvent(null); resetForm(); setShowCreateDialog(true); }}
                    data-testid="post-event-button"
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
                  >
                    <Plus size={16} /> Post Event
                  </button>
                )}
              </div>
            </div>

            {communityLoading ? (
              <div className="text-center text-[#4B6B58] animate-pulse py-8">Loading events...</div>
            ) : communityEvents.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-events-grid">
                {communityEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    isCommunity={true}
                    isOwner={event.user_id === user?.id}
                    onEdit={openEdit}
                    onDelete={handleDeleteEvent}
                    onChat={handleEventChat}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-[2rem] p-10 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]">
                <Users size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
                <p className="text-sm text-[#4B6B58]">No community events yet. Be the first to post!</p>
                <p className="text-xs text-[#4B6B58] mt-1">Or discover events using the AI search and save them here.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit Event Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">
              {editEvent ? 'Edit Event' : 'Post New Event'}
            </DialogTitle>
            <DialogDescription className="text-[#4B6B58]">
              Share an equine event with the community.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEvent} className="space-y-3 mt-4">
            <div>
              <label className="block text-xs font-medium text-[#122A1F] mb-1">Event Title *</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required data-testid="event-title-input" className="stable-input w-full" placeholder="e.g. Spring Horse Show" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">Event Type</label>
                <select value={form.event_type} onChange={e => setForm({...form, event_type: e.target.value})} data-testid="event-type-input" className="stable-input w-full">
                  {EVENT_TYPES.filter(t => t).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">Cost</label>
                <input value={form.cost} onChange={e => setForm({...form, cost: e.target.value})} data-testid="event-cost-input" className="stable-input w-full" placeholder="e.g. $25 or Free" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">Start Date *</label>
                <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required data-testid="event-date-input" className="stable-input w-full" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">End Date</label>
                <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="event-end-date-input" className="stable-input w-full" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#122A1F] mb-1">Location / Venue</label>
              <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="event-location-input" className="stable-input w-full" placeholder="Venue name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">Address</label>
                <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="event-address-input" className="stable-input w-full" placeholder="City, ST" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">Zip</label>
                <input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value.replace(/\D/g, '').slice(0, 5)})} data-testid="event-zip-input" className="stable-input w-full" placeholder="Zip" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">State</label>
                <select value={form.state} onChange={e => setForm({...form, state: e.target.value})} data-testid="event-state-input" className="stable-input w-full">
                  <option value="">—</option>
                  {US_STATES.filter(s => s).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#122A1F] mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="event-desc-input" className="stable-input w-full" rows={2} placeholder="What's this event about?" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">Website</label>
                <input value={form.website} onChange={e => setForm({...form, website: e.target.value})} data-testid="event-website-input" className="stable-input w-full" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#122A1F] mb-1">Contact</label>
                <input value={form.contact_info} onChange={e => setForm({...form, contact_info: e.target.value})} data-testid="event-contact-input" className="stable-input w-full" placeholder="Phone or email" />
              </div>
            </div>
            <button type="submit" data-testid="event-save-button" className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">
              {editEvent ? 'Update Event' : 'Post Event'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
